import React, { useCallback, useEffect, useRef, useState } from 'react';
import './styles/global.css';
import BottomNav from './components/BottomNav';
import Icon from './components/Icon';
import { LoadingBlock, Toast } from './components/ui';
import AuthScreen from './screens/AuthScreen';
import ChatScreen from './screens/ChatScreen';
import DiscoverScreen from './screens/DiscoverScreen';
import MessagesScreen from './screens/MessagesScreen';
import MyProfileScreen from './screens/MyProfileScreen';
import UserProfileScreen from './screens/UserProfileScreen';
import { api } from './lib/api';

const TOAST_MS = 3200;
const INBOX_POLL_MS = 20000;

export default function App() {
  const [booting, setBooting] = useState(true);
  const [token, setToken] = useState(null);
  const [me, setMe] = useState(null);

  const [tab, setTab] = useState('discover');
  const [stack, setStack] = useState([]);

  const [people, setPeople] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [inboxError, setInboxError] = useState('');

  const [resetToken, setResetToken] = useState(null);
  const [verifyNotice, setVerifyNotice] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = useCallback((message, kind = 'success') => {
    window.clearTimeout(toastTimer.current);
    setToast({ message, kind });
    toastTimer.current = window.setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  /* ---------- Session bootstrap + URL tokens ---------- */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verifyParam = params.get('verifyToken');
    const resetParam = params.get('resetToken');
    if (verifyParam || resetParam) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (resetParam) setResetToken(resetParam);
    if (verifyParam) {
      api
        .get(`/api/auth/verify-email?token=${encodeURIComponent(verifyParam)}`)
        .then((data) => setVerifyNotice({ ok: true, message: data?.message || 'Email verified — you are all set.' }))
        .catch((err) => setVerifyNotice({ ok: false, message: err.message }));
    }

    const saved = window.localStorage.getItem('token');
    if (!saved) {
      setBooting(false);
      return;
    }
    api
      .get('/api/auth/profile', saved)
      .then((data) => {
        if (!data?.username) throw new Error('Session expired');
        setMe({
          id: data._id,
          username: data.username,
          email: data.email,
          bio: data.bio,
          profileImage: data.profileImage,
          photos: data.photos || [],
          isEmailVerified: data.isEmailVerified,
        });
        setToken(saved);
      })
      .catch(() => window.localStorage.removeItem('token'))
      .finally(() => setBooting(false));
  }, []);

  /* ---------- Data loading ---------- */
  const loadPeople = useCallback(async () => {
    setPeopleLoading(true);
    try {
      const data = await api.get('/api/auth/all');
      setPeople(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setPeopleLoading(false);
    }
  }, [showToast]);

  const loadInbox = useCallback(
    async ({ silent = false } = {}) => {
      if (!token) return;
      if (!silent) setInboxLoading(true);
      try {
        const data = await api.get('/api/chat/inbox/all', token);
        setConversations(Array.isArray(data) ? data : []);
        setInboxError('');
      } catch (err) {
        if (!silent) setInboxError(err.message);
      } finally {
        if (!silent) setInboxLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (!token) return;
    loadPeople();
    loadInbox();
  }, [token, loadPeople, loadInbox]);

  useEffect(() => {
    if (!token) return undefined;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') loadInbox({ silent: true });
    }, INBOX_POLL_MS);
    return () => window.clearInterval(id);
  }, [token, loadInbox]);

  /* ---------- Navigation ---------- */
  useEffect(() => {
    const onPopState = () => setStack((prev) => prev.slice(0, -1));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const push = useCallback((entry) => {
    window.history.pushState({ date9: true }, '');
    setStack((prev) => [...prev, entry]);
  }, []);

  const pop = useCallback(() => window.history.back(), []);

  const openChat = useCallback(
    (person) => {
      push({ type: 'chat', person });
      loadInbox({ silent: true });
    },
    [push, loadInbox]
  );

  const openProfile = useCallback((person) => push({ type: 'profile', person }), [push]);

  const switchTab = (next) => {
    setTab(next);
    if (next === 'messages') loadInbox({ silent: conversations.length > 0 });
  };

  /* ---------- Session actions ---------- */
  const signIn = (user, authToken) => {
    window.localStorage.setItem('token', authToken);
    setMe({ ...user, photos: user.photos || [] });
    setToken(authToken);
    setTab('discover');
  };

  const signOut = () => {
    window.localStorage.removeItem('token');
    setToken(null);
    setMe(null);
    setStack([]);
    setPeople([]);
    setConversations([]);
    setTab('discover');
  };

  const updateMe = (patch) => setMe((prev) => ({ ...prev, ...patch }));

  const resendVerification = async () => {
    try {
      await api.post('/api/auth/resend-verification', { email: me.email });
      showToast('Verification email sent — check your inbox.', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  /* ---------- Render ---------- */
  if (booting) {
    return (
      <div className="auth-page">
        <LoadingBlock text="Getting things ready…" />
      </div>
    );
  }

  if (!me || !token) {
    return (
      <AuthScreen
        resetToken={resetToken}
        onResetDone={() => setResetToken(null)}
        verifyNotice={verifyNotice}
        onDismissVerifyNotice={() => setVerifyNotice(null)}
        onSignedIn={signIn}
      />
    );
  }

  const overlay = stack[stack.length - 1];

  if (overlay?.type === 'chat') {
    return (
      <>
        <ChatScreen
          me={me}
          recipient={overlay.person}
          token={token}
          onBack={pop}
          onOpenProfile={openProfile}
        />
        <Toast toast={toast} />
      </>
    );
  }

  if (overlay?.type === 'profile') {
    return (
      <>
        <UserProfileScreen person={overlay.person} onBack={pop} onMessage={openChat} />
        <Toast toast={toast} />
      </>
    );
  }

  return (
    <div className="app-shell">
      {verifyNotice && (
        <div
          className="notice-bar"
          style={{
            background: verifyNotice.ok ? 'var(--success-soft)' : 'var(--danger-soft)',
            border: `1px solid ${verifyNotice.ok ? 'rgba(52,211,153,0.28)' : 'rgba(240,87,111,0.28)'}`,
            color: verifyNotice.ok ? '#6ee7b7' : '#ff9caa',
          }}
        >
          <Icon name={verifyNotice.ok ? 'checkCircle' : 'alert'} size={17} />
          <span className="notice-bar-text">{verifyNotice.message}</span>
          <button
            type="button"
            className="notice-bar-close"
            onClick={() => setVerifyNotice(null)}
            aria-label="Dismiss notification"
          >
            <Icon name="x" size={16} />
          </button>
        </div>
      )}

      {tab === 'discover' && (
        <DiscoverScreen
          me={me}
          people={people}
          loading={peopleLoading && people.length === 0}
          onRefresh={loadPeople}
          onOpenProfile={openProfile}
          onOpenChat={openChat}
          showToast={showToast}
        />
      )}

      {tab === 'messages' && (
        <MessagesScreen
          me={me}
          conversations={conversations}
          loading={inboxLoading}
          error={inboxError}
          onRefresh={() => loadInbox()}
          onOpenChat={openChat}
          onGoDiscover={() => setTab('discover')}
        />
      )}

      {tab === 'profile' && (
        <MyProfileScreen
          me={me}
          token={token}
          onUpdate={updateMe}
          onSignOut={signOut}
          onResendVerification={resendVerification}
          showToast={showToast}
        />
      )}

      <BottomNav active={tab} onChange={switchTab} />
      <Toast toast={toast} />
    </div>
  );
}
